import { useCallback, useEffect, useState } from "react";

import { useAuth } from "../../../contexts/auth2";
import { handlePromise } from "../../../utils/handlePromisse";
import { favoriteOral, favoriteOralDelete, putOral } from "../../../services/oralService";
import { favoriteProduct, getPaginatedProducts, postAnnotation, unFavoriteProduct } from "../../../services/productService";
import { ActiveModel } from "./active-model";
import { filterByType, filterToQueryParams, isOral, isVehicle, LibraryFilter } from "./library-filters";

// ----------------------------------------------------------------------

const PAGE_SIZE = 30;
const SEARCH_DEBOUNCE = 350;

export default function useActivesLibrary() {
  const { user } = useAuth();
  const [items, setItems] = useState<ActiveModel[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loadedRaw, setLoadedRaw] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [filter, setFilter] = useState<LibraryFilter>("todos");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), SEARCH_DEBOUNCE);
    return () => clearTimeout(timer);
  }, [query]);

  const fetchPage = useCallback(
    async (targetPage: number, mode: "reset" | "append") => {
      if (mode === "reset") setLoading(true);
      else setLoadingMore(true);

      const { type, onlyFavorites } = filterToQueryParams(filter);
      const [result, fetchError] = await handlePromise(
        getPaginatedProducts(targetPage, PAGE_SIZE, undefined, undefined, debouncedQuery || undefined, undefined, type, onlyFavorites)
      );

      if (mode === "reset") setLoading(false);
      else setLoadingMore(false);

      if (fetchError) {
        setError("Erro ao carregar a biblioteca de ativos.");
        return;
      }
      setError(null);

      const raw: ActiveModel[] = result.data.data ?? [];
      const safe = filterByType(raw, filter);
      setTotal(result.data.pagination?.total ?? raw.length);
      setPage(targetPage);

      if (mode === "reset") {
        setItems(safe);
        setLoadedRaw(raw.length);
      } else {
        setItems((current) => [...current, ...safe]);
        setLoadedRaw((current) => current + raw.length);
      }
    },
    [filter, debouncedQuery]
  );

  useEffect(() => {
    fetchPage(1, "reset");
  }, [fetchPage]);

  const showMore = useCallback(() => fetchPage(page + 1, "append"), [fetchPage, page]);

  const patchItem = useCallback((target: ActiveModel, patch: Partial<ActiveModel>) => {
    setItems((current) =>
      current.map((item) => (item.id === target.id && item.type === target.type ? { ...item, ...patch } : item))
    );
  }, []);

  /** Otimista; devolve false (e reverte) em erro */
  const toggleFavorite = useCallback(
    async (item: ActiveModel): Promise<boolean> => {
      if (isVehicle(item)) return false;
      const next = !item.favorite;
      patchItem(item, { favorite: next });

      const request = isOral(item)
        ? next
          ? favoriteOral(item.id)
          : favoriteOralDelete(item.id)
        : next
          ? favoriteProduct({ id_Product: item.id, id_User: user.idUser })
          : unFavoriteProduct(user.idUser, item.id);

      const [, requestError] = await handlePromise(request);
      if (requestError) {
        patchItem(item, { favorite: !next });
        return false;
      }
      return true;
    },
    [patchItem, user.idUser]
  );

  /** Salva anotação (tópico ou oral) e atualiza o item local */
  const saveAnnotation = useCallback(
    async (item: ActiveModel, annotation: string): Promise<boolean> => {
      const request = isOral(item) ? putOral(item.id, annotation) : postAnnotation(item.id, annotation);
      const [, requestError] = await handlePromise(request);
      if (requestError) return false;
      patchItem(item, { annotation });
      return true;
    },
    [patchItem]
  );

  return {
    items,
    filtered: items,
    visible: items,
    total,
    loading,
    loadingMore,
    error,
    query,
    setQuery,
    filter,
    setFilter,
    showMore,
    hasMore: loadedRaw < total,
    toggleFavorite,
    saveAnnotation,
  };
}
